// frontend/src/contexts/AuthContext.tsx - ENHANCED VERSION WITH VALIDATORS
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { errorStatusForLog } from '../lib/apiError';
import { getErrorCode, getFieldErrors, getErrorMessage, errorText, rateLimitMessage } from '../lib/apiError';
import { User, LoginRequest, LoginResponse } from '../types';
import { api } from '../lib/apiClient';
import { clearPlatformMfaToken, MFA_VERIFIED_EVENT } from '../lib/platformMfaToken';
import { toast } from 'react-hot-toast';
import { loginUrlFor, redirectOnce } from '../lib/loginRedirect';
import { classifyVerifyError, type VerifyResult } from '../lib/authVerify';
import { analytics } from '../lib/analytics'; // 📊 GTM Analytics

// Debug configuration
const DEBUG_AUTH = false; // Set to false to disable auth logs

// Types
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** 422 de login: errores por campo (email/password) ya traducidos. */
  fieldErrors: Record<string, string>;
  token: string | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
  refreshAuth: () => Promise<void>;
  isTokenExpired: () => boolean;
  timeUntilExpiration: () => number | null;
  clearError: () => void;
  /** true si habia una sesion guardada y resulto invalida (vencida o rechazada); false si nunca hubo sesion. */
  sessionExpired: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ===== VALIDATION UTILITIES =====
const ValidationUtils = {
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  validateLoginInput(email: string, password: string): { valid: boolean; error?: string } {
    if (!email || !email.trim()) {
      return { valid: false, error: 'Email is required.' };
    }

    if (!this.isValidEmail(email)) {
      return { valid: false, error: 'Please enter a valid email address.' };
    }

    if (!password || !password.trim()) {
      return { valid: false, error: 'Password is required.' };
    }

    if (password.length < 6) {
      return { valid: false, error: 'Password must be at least 6 characters long.' };
    }

    return { valid: true };
  }
};

// ===== ERROR HANDLING UTILITIES =====
const ErrorUtils = {
  parseLoginError(error: any): string {
    // Check for browser extension interference
    if (error.message?.includes('listener') ||
      error.message?.includes('message channel') ||
      error.message?.includes('chrome-extension')) {
      return 'Browser extension is blocking the login. Please:\n' +
        '• Disable browser extensions\n' +
        '• Try incognito mode (Ctrl+Shift+N)\n' +
        '• Use a different browser';
    }

    // Timeout error
    if (error.message === 'Request timeout' || error.code === 'ECONNABORTED') {
      return 'Connection timeout. The server took too long to respond. ' +
        'Please check your internet connection and try again.';
    }

    // HTTP: nunca el mensaje del backend. 400/401 en login = INVALID_CREDENTIALS generico
    // (no revela si el email existe). 422 -> resumen + errores por campo (fieldErrors).
    const st = error.response?.status;
    if (st) {
      if (st === 400 || st === 401 || getErrorCode(error) === 'INVALID_CREDENTIALS') return errorText('errors.invalid_credentials');
      if (st === 429) return rateLimitMessage(error);
      return getErrorMessage(error, errorText('errors.unknown'));
    }

    const messageText = (error?.message || '').toString();
    if (messageText.toLowerCase().includes('invalid credentials') || messageText.includes('401')) {
      return errorText('errors.invalid_credentials');
    }

    const messageLower = messageText.toLowerCase();
    const isNetworkLike =
      error.code === 'ERR_NETWORK' ||
      messageLower.includes('network error') ||
      messageLower.includes('failed to fetch') ||
      messageLower.includes('load failed') ||
      messageLower.includes('err_connection');

    if (isNetworkLike) {
      return 'Network error. Please check your internet connection and try again.';
    }

    // Default: texto generico (nunca error.message ni detail crudo)
    return errorText('errors.unknown');
  }
};

// ===== TOKEN UTILITIES - OPTIMIZED =====
const TokenUtils = {
  isTokenExpired(): boolean {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return true;

      const payload = JSON.parse(atob(token.split('.')[1]));
      const expTime = payload.exp * 1000;
      const now = Date.now();

      const isExpired = now >= expTime;

      if (DEBUG_AUTH && isExpired) {
        console.log('🔓 Token expired');
      }

      return isExpired;
    } catch (error: unknown) {
      if (DEBUG_AUTH) {
        console.warn('⚠️ Could not decode token:', errorStatusForLog(error));
      }
      return true;
    }
  },

  timeUntilExpiration(): number | null {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return null;

      const payload = JSON.parse(atob(token.split('.')[1]));
      const expTime = payload.exp * 1000;
      const now = Date.now();

      return Math.max(0, expTime - now);
    } catch (error: unknown) {
      return null;
    }
  },

  isTokenExpiringSoon(thresholdMinutes: number = 5): boolean {
    const timeLeft = this.timeUntilExpiration();
    if (!timeLeft) return true;

    const thresholdMs = thresholdMinutes * 60 * 1000;
    return timeLeft < thresholdMs;
  },

  getTokenInfo(): any {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return null;

      const payload = JSON.parse(atob(token.split('.')[1]));
      const expTime = payload.exp * 1000;
      const now = Date.now();

      return {
        payload,
        expTime: new Date(expTime).toISOString(),
        now: new Date(now).toISOString(),
        timeLeft: Math.max(0, expTime - now),
        minutesLeft: Math.floor(Math.max(0, expTime - now) / (1000 * 60)),
        isExpired: now >= expTime
      };
    } catch (error: unknown) {
      return {
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
};

// Auth Provider Component
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isInitialized, setIsInitialized] = useState(false);

  // ===== TOKEN EXPIRATION MONITORING - OPTIMIZED =====
  useEffect(() => {
    let tokenCheckInterval: NodeJS.Timeout;
    let expirationWarningShown = false;

    if (isAuthenticated) {
      if (DEBUG_AUTH) console.log('🔄 Starting token monitoring...');

      tokenCheckInterval = setInterval(() => {
        const tokenInfo = TokenUtils.getTokenInfo();

        if (tokenInfo && !tokenInfo.error) {
          const minutesLeft = tokenInfo.minutesLeft;

          // Show warning when 5 minutes left
          if (minutesLeft <= 5 && minutesLeft > 0 && !expirationWarningShown) {
            if (DEBUG_AUTH) console.warn('⏰ Session expires in … minute(s)');
            showExpirationWarning(minutesLeft);
            expirationWarningShown = true;
          }

          // Auto-logout when expired
          if (tokenInfo.isExpired) {
            if (DEBUG_AUTH) console.log('🔓 Token expired, logging out...');
            handleTokenExpiration();
          }
        } else {
          if (DEBUG_AUTH) console.log('🔓 Invalid token, logging out...');
          handleTokenExpiration();
        }
      }, 30000); // Check every 30 seconds

      const resetWarningFlag = () => {
        expirationWarningShown = false;
      };

      window.addEventListener('auth:token-refreshed', resetWarningFlag);

      return () => {
        if (DEBUG_AUTH) console.log('🛑 Stopping token monitoring...');
        if (tokenCheckInterval) {
          clearInterval(tokenCheckInterval);
        }
        window.removeEventListener('auth:token-refreshed', resetWarningFlag);
      };
    }
  }, [isAuthenticated]);

  // Habia sesion guardada y no sirve: limpiar y recordar el motivo para /login?reason=session_expired.
  const expireSession = () => {
    clearAuth();
    setSessionExpired(true);
  };

  useEffect(() => {
    if (isAuthenticated) setSessionExpired(false);
  }, [isAuthenticated]);

  // Restaura el usuario guardado en localStorage (si es JSON valido). Devuelve true si habia.
  const restoreSavedUser = (): boolean => {
    const saved = localStorage.getItem('auth_user');
    if (!saved) return false;
    try {
      setUser(JSON.parse(saved));
      return true;
    } catch {
      return false;
    }
  };

  // ===== INITIALIZATION - OPTIMIZED =====
  useEffect(() => {
    if (!isInitialized) {
      checkInitialAuth();
    }
  }, []); // Empty deps - run only once

  // ===== AUTH METHODS =====
  const checkInitialAuth = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('auth_user');

      if (DEBUG_AUTH) {
        console.log('🔍 Checking initial auth:');
      }

      if (token && savedUser) {
        // Check if token is expired
        if (TokenUtils.isTokenExpired()) {
          if (DEBUG_AUTH) console.log('🔓 Stored token expired, clearing auth...');
          expireSession();
          return;
        }

        try {
          // Verify token with server
          const result = await verifyToken();
          if (result === 'valid') {
            restoreSavedUser();
            setIsAuthenticated(true);
            if (DEBUG_AUTH) console.log('Auth restored');
          } else if (result === 'transient') {
            // 5xx / red / timeout: no se pudo verificar; se conservan tokens y usuario guardado.
            if (restoreSavedUser()) setIsAuthenticated(true);
          } else {
            if (DEBUG_AUTH) console.log('Token verification failed');
            expireSession();
          }
        } catch (error: unknown) {
          if (DEBUG_AUTH) console.log('❌ Token verification error');
          expireSession();
        }
      }
    } catch (error: unknown) {
      if (DEBUG_AUTH) console.error('❌ Initial auth check failed');
      clearAuth();
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  const verifyToken = async (): Promise<VerifyResult> => {
    try {
      const currentUser = await api.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        localStorage.setItem('auth_user', JSON.stringify(currentUser));
        if (typeof window !== 'undefined' && currentUser.date_format) {
          localStorage.setItem('appearance_date_format', currentUser.date_format);
        }
        return 'valid';
      }
      return 'invalid';
    } catch (error: unknown) {
      if (DEBUG_AUTH) console.error('❌ Token verification failed');
      return classifyVerifyError(error);
    }
  };

  const login = async (email: string, password: string, rememberMe: boolean = false): Promise<void> => {
    clearPlatformMfaToken(); // re-login invalida el X-MFA-Token del staff
    // Clear any previous errors
    setError(null);
    setFieldErrors({});
    setIsLoading(true);

    try {

      // ✅ STEP 1: Validate input
      const validation = ValidationUtils.validateLoginInput(email, password);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      if (DEBUG_AUTH) console.log('✅ Input validation passed');

      // ✅ STEP 2: Prepare login request
      const loginRequest: LoginRequest = {
        email: email.trim().toLowerCase(),
        password,
        remember_me: rememberMe
      };

      // ✅ STEP 3: Create timeout promise (30 seconds)
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 30000)
      );

      // ✅ STEP 4: Make login API call with timeout
      if (DEBUG_AUTH) console.log('📡 Sending login request to API...');

      const loginPromise = api.login(loginRequest);
      const response: LoginResponse = await Promise.race([loginPromise, timeoutPromise]);

      if (DEBUG_AUTH) console.log('✅ Login API response received');

      // ✅ STEP 5: Validate response
      if (!response || !response.access_token) {
        throw new Error('Invalid response from server. Please try again.');
      }

      if (!response.user) {
        throw new Error('User data not received. Please try again.');
      }


      // ✅ STEP 6: Store authentication data
      localStorage.setItem('auth_token', response.access_token);
      localStorage.setItem('auth_user', JSON.stringify(response.user));

      if (response.refresh_token) {
        localStorage.setItem('refresh_token', response.refresh_token);
        if (DEBUG_AUTH) console.log('🔄 Refresh token stored');
      }

      setUser(response.user);
      setIsAuthenticated(true);
      setError(null);

      try {
        const me = await api.getCurrentUser();
        if (me) {
          setUser(me);
          localStorage.setItem('auth_user', JSON.stringify(me));
          if (typeof window !== 'undefined' && me.date_format) {
            localStorage.setItem('appearance_date_format', me.date_format);
          }
          analytics.setUserProperties({
            userId: me.id?.toString(),
            userRole: me.role,
            tenantId: me.tenant_id?.toString(),
          });
        }
      } catch {}

      // 📊 Track successful login
      analytics.trackLogin(
        response.user.id?.toString() || '',
        response.user.role,
        'email'
      );
      analytics.setUserProperties({
        userId: response.user.id?.toString(),
        userRole: response.user.role,
        tenantId: response.user.tenant_id?.toString(),
      });

      if (DEBUG_AUTH) console.log('✅ Auth state updated successfully');

    } catch (error: any) {
      if (DEBUG_AUTH) {
        console.error('❌ Login failed:', errorStatusForLog(error));
        console.error('❌ Error details:');
      }

      // Clear any partial auth state
      clearAuth();

      // Parse error and set specific message
      const errorMessage = ErrorUtils.parseLoginError(error);
      setError(errorMessage);
      setFieldErrors(error?.response?.status === 422 ? getFieldErrors(error) : {});

      // Re-throw with parsed message
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    if (DEBUG_AUTH) console.log('🔓 Logging out');

    // 📊 Track logout
    analytics.trackLogout();

    api.logout().catch(() => { });

    clearAuth();

    if (window.location.pathname !== '/login' &&
      window.location.pathname !== '/register' &&
      window.location.pathname !== '/') {
      redirectOnce(loginUrlFor());
    }
  };

  const clearAuth = () => {
    clearPlatformMfaToken(); // sincrono: el X-MFA-Token no sobrevive al logout
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token'); // Legacy compatibility

    setUser(null);
    setIsAuthenticated(false);
    setError(null);

    if (DEBUG_AUTH) console.log('🧹 Auth cleared');
  };

  const clearError = () => {
    setError(null);
    setFieldErrors({});
  };

  const checkAuth = useCallback(async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return false;
      if (TokenUtils.isTokenExpired()) {
        expireSession();
        return false;
      }
      const result = await verifyToken();
      if (result === 'invalid') {
        expireSession();
        return false;
      }
      // valid: verifyToken solo fija user; sin esto, tras signup (p.ej. cupon 100%) checkAuth()=true
      // pero isAuthenticated seguia false y ProtectedRoute mandaba a /login.
      // transient (5xx, red, timeout): se conserva la sesion guardada, sin expirar ni redirigir.
      if (result === 'transient' && !restoreSavedUser()) return false;
      setIsAuthenticated(true);
      return true;
    } catch (error: unknown) {
      expireSession();
      return false;
    }
  }, []);

  const refreshAuth = useCallback(async (): Promise<void> => {
    try {
      if (DEBUG_AUTH) console.log('🔄 Refreshing auth...');
      await fetchCurrentUser();
    } catch (error: any) {
      if (DEBUG_AUTH) console.error('❌ Auth refresh failed', errorStatusForLog(error));

      // CRITICAL FIX: Only clear auth if it's a 401 (Unauthorized)
      // Do NOT clear on network errors or other server errors
      if (error.response?.status === 401) {
        expireSession();
      }
      throw error;
    }
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const currentUser = await api.getCurrentUser();

      if (currentUser) {
        setUser(currentUser);
        setIsAuthenticated(true);
        localStorage.setItem('auth_user', JSON.stringify(currentUser));
        if (typeof window !== 'undefined' && currentUser.date_format) {
          localStorage.setItem('appearance_date_format', currentUser.date_format);
        }
      } else {
        throw new Error('No user data received');
      }
    } catch (error: unknown) {
      if (DEBUG_AUTH) console.error('❌ Failed to fetch current user');
      throw error;
    }
  }, []);

  // ===== TOKEN EXPIRATION HANDLERS =====
  const handleTokenExpiration = () => {
    if (DEBUG_AUTH) console.log('🔓 Handling token expiration...');

    expireSession();

    if (window.location.pathname !== '/login' &&
      window.location.pathname !== '/register' &&
      window.location.pathname !== '/') {
      const message = 'Your session has expired. Please log in again.';

      if ('showTokenExpiredNotification' in window) {
        (window as any).showTokenExpiredNotification(message);
      } else {
        toast.error(message);
      }

      redirectOnce(loginUrlFor('session_expired'));
    }
  };

  const showExpirationWarning = (minutesLeft: number) => {
    const message = `Your session will expire in ${minutesLeft} minute(s). Please save your work.`;

    if ('showSessionWarning' in window) {
      (window as any).showSessionWarning(message, minutesLeft);
    } else {
      if (DEBUG_AUTH) console.warn('⏰ …');
    }
  };

  // ===== TOKEN INFO METHODS =====
  const isTokenExpired = (): boolean => {
    return TokenUtils.isTokenExpired();
  };

  const timeUntilExpiration = (): number | null => {
    return TokenUtils.timeUntilExpiration();
  };

  // 2FA staff: /me pedido con X-MFA-Token tras verificar (refreshMeWithMfa) => aplica el usuario si es el mismo.
  useEffect(() => {
    const onVerified = (e: Event) => {
      const next = (e as CustomEvent<{ user?: User }>).detail?.user;
      if (!next || !next.id) return;
      setUser((prev) => {
        if (!prev || prev.id !== next.id) return prev;
        try { localStorage.setItem('auth_user', JSON.stringify(next)); } catch { /* noop */ }
        return next;
      });
    };
    window.addEventListener(MFA_VERIFIED_EVENT, onVerified);
    return () => window.removeEventListener(MFA_VERIFIED_EVENT, onVerified);
  }, []);

  // ===== CONTEXT VALUE =====
  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    fieldErrors,
    token: typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null,
    login,
    logout,
    checkAuth,
    refreshAuth,
    isTokenExpired,
    timeUntilExpiration,
    clearError,
    sessionExpired
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ===== HOOKS =====
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** Like useAuth, but returns undefined outside AuthProvider (public components and tests). */
export function useOptionalAuth(): AuthContextType | undefined {
  return useContext(AuthContext);
}

export function useAuthRole(requiredRole?: string) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return { hasAccess: false, userRole: null };
  }

  if (!requiredRole) {
    return { hasAccess: true, userRole: user.role };
  }

  const roleHierarchy = ['employee', 'manager', 'owner', 'admin'];
  const userRoleIndex = roleHierarchy.indexOf(user.role);
  const requiredRoleIndex = roleHierarchy.indexOf(requiredRole);

  const hasAccess = userRoleIndex >= requiredRoleIndex;

  return { hasAccess, userRole: user.role };
}

export function useTokenMonitor() {
  const { isTokenExpired, timeUntilExpiration } = useAuth();
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);

  useEffect(() => {
    const updateTimeLeft = () => {
      const time = timeUntilExpiration();
      setTimeLeft(time);
      setIsExpiringSoon(time !== null && time < 5 * 60 * 1000);
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [timeUntilExpiration]);

  return {
    timeLeft,
    isExpiringSoon,
    isExpired: isTokenExpired(),
    minutesLeft: timeLeft ? Math.floor(timeLeft / (1000 * 60)) : null,
    tokenInfo: DEBUG_AUTH ? TokenUtils.getTokenInfo() : null
  };
}
